import { useEffect, useMemo } from 'react';
import { SelectionManager, useMultipleSelectionState } from '../../@react-stately/selection';
import { useControlledState } from '../../@react-stately/utils';
import { TreeCollection } from './ReactAriaTreeCollection';
function getItemNode(item, index, parentItem, level = 0) {
    var _item_childItems, _item_childItems1;
    return {
        type: item.type,
        props: item.data,
        rendered: null,
        textValue: item.textValue || '',
        'aria-label': item['aria-label'],
        hasChildNodes: Boolean((_item_childItems = item.childItems) === null || _item_childItems === void 0 ? void 0 : _item_childItems.length),
        childNodes: ((_item_childItems1 = item.childItems) === null || _item_childItems1 === void 0 ? void 0 : _item_childItems1.map((child, i)=>getItemNode(child, i, item, level + 1))) || [],
        key: item.key,
        parentKey: parentItem ? parentItem.key : undefined,
        level,
        index,
        value: item.data
    };
}
export function toReactAriaCollection(getItems, filter) {
    const items = filter ? filter(getItems()) : getItems();
    return items.map((item, i)=>getItemNode(item, i));
}
function toggleKey(set, key) {
    const res = new Set(set);
    if (res.has(key)) {
        res.delete(key);
    } else {
        res.add(key);
    }
    return res;
}
/**
 * Provides state management for tree-like components. Handles building a collection
 * of items from props, item expanded state, and manages multiple selection state.
 */ export function useReactAriaTreeState({ expandedKeys, defaultExpandedKeys, onExpandedChange, disabledKeys, getItems, filter, defaultSelectedKeys, selectedKeys, disallowEmptySelection, onSelectionChange, selectionMode, expandable }) {
    const allKeys = ()=>getItems().map((x)=>x.key);
    const [expandedKeysSet, setExpandedKeys] = useControlledState(expandedKeys ? new Set(expandedKeys) : undefined, defaultExpandedKeys ? new Set(defaultExpandedKeys === 'all' ? allKeys() : defaultExpandedKeys) : new Set(), onExpandedChange);
    const selectionState = useMultipleSelectionState({
        selectionMode,
        disallowEmptySelection,
        allowDuplicateSelectionEvents: true,
        selectedKeys,
        defaultSelectedKeys,
        onSelectionChange,
        disabledKeys
    });
    const disabledKeysSet = useMemo(()=>disabledKeys ? new Set(disabledKeys) : new Set(), [
        disabledKeys
    ]);
    const collection = useMemo(()=>new TreeCollection(toReactAriaCollection(getItems, filter), {
            expandedKeys: expandedKeysSet
        }), [
        getItems,
        filter,
        expandedKeysSet
    ]);
    // Reset focused key if that item is deleted from the collection.
    useEffect(()=>{
        if (selectionState.focusedKey != null && !collection.getItem(selectionState.focusedKey)) {
            selectionState.setFocusedKey(null);
        }
    // This is copied from react-aria. We cannot add `selectionState.setFocusedKey`
    // here as it's a new function each render. All that function does it set refs
    // so this is safe, but we must disabled this rule because of react-aria's code
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        collection,
        selectionState.focusedKey
    ]);
    const onToggle = (key)=>{
        setExpandedKeys(toggleKey(expandedKeysSet, key));
    };
    return {
        collection,
        expandedKeys: expandedKeysSet,
        disabledKeys: disabledKeysSet,
        toggleKey: onToggle,
        selectionManager: new SelectionManager(collection, selectionState),
        setExpandedKeys,
        expandable: Boolean(expandable)
    };
}

//# sourceMappingURL=useReactAriaTreeState.js.map